import React, { useCallback, useEffect, useRef, useState } from "react";
import { hasCap } from "../utils/capabilities";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { listEdges, issueDeviceClaim, registerEdge } from "../api/edgeApi";
import { getTenants } from "../api/tenantApi";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  FormControlLabel,
  Switch,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import { formatDate } from "../utils/i18nFormat";


export default function EdgeList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const accessToken = useSelector((s) => s.session?.accessToken);
  const me = useSelector((s) => s.session?.me);
  const isSuperAdmin = me?.global_role === "superadmin";
  const canIssueClaim = hasCap(me, "edge.claim");

  const [edges, setEdges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const PAGE_SIZE = 50;
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);

  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [createdEdgeId, setCreatedEdgeId] = useState("");
  const [claimModalOpen, setClaimModalOpen] = useState(false);
  const [claimCode, setClaimCode] = useState("");
  const [claimEdgeId, setClaimEdgeId] = useState("");
  const [claimEdgeUuid, setClaimEdgeUuid] = useState("");
  const [claimExpiresAt, setClaimExpiresAt] = useState("");
  const [claimLoadingId, setClaimLoadingId] = useState(null);
  const claimRequest = useRef(0);

  // Form states
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState("");
  const [edgeId, setEdgeId] = useState("");
  const [model, setModel] = useState("");
  const [firmware, setFirmware] = useState("");
  const [cloudTransferEnabled, setCloudTransferEnabled] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Debounce the search box so every keystroke doesn't fire a request.
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);
      setSearch(searchInput.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchEdges = useCallback(async () => {
    if (!accessToken) return;
    try {
      setLoading(true);
      const { items, total: count } = await listEdges(accessToken, {
        q: search || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setEdges(items);
      setTotal(count);
    } catch (err) {
      toast.error(err.response?.data?.detail || i18n.t("edgeDetail.list.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [accessToken, search, page]);

  useEffect(() => {
    fetchEdges();
  }, [fetchEdges]);


  // Fetch tenants (for superadmin)
  useEffect(() => {
    if (!isSuperAdmin || !accessToken) return;

    async function fetchTenants() {
      try {
        const data = await getTenants(accessToken, { limit: 100 });
        setTenants(data.items || []);
      } catch (err) {
        console.error("Could not load tenants:", err);
      }
    }

    fetchTenants();
  }, [isSuperAdmin, accessToken]);

  // Open create modal
  const handleOpenCreateModal = () => {
    setCreateModalOpen(true);
    setSelectedTenantId("");
    setEdgeId("");
    setModel("");
    setFirmware("");
    setCloudTransferEnabled(false);
  };

  // Close create modal
  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
  };

  const handleSubmitCreate = async () => {
    if (!selectedTenantId || !edgeId.trim()) {
      toast.error(t("edgeDetail.list.required"));
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        edgeId: edgeId.trim(),
        location_id: null,
        cloud_transfer_enabled: cloudTransferEnabled,
        device: {
          manufacturer: "",
          model: model.trim() || "",
          firmware: firmware.trim() || "",
        },
        asset: {
          assetId: "",
          type: "",
          location: "",
        },
      };

      const result = await registerEdge(accessToken, selectedTenantId, payload);

      setCreatedEdgeId(result.id); // DB'deki UUID
      toast.success(t("edgeDetail.list.created"));
      if (cloudTransferEnabled) {
        toast.warning(t("edgeDetail.list.licenseReminder"), { autoClose: 10000 });
      }
      setCreateModalOpen(false);
      try {
        const requestId = ++claimRequest.current;
        const claim = await issueDeviceClaim(accessToken, result.id);
        if (requestId !== claimRequest.current) return;
        if (claim.edge_uuid !== result.id) {
          toast.error(t("edgeDetail.list.claimWrongEdge"));
          return;
        }
        openClaimModal(claim);
      } catch (claimError) {
        setSuccessModalOpen(true);
        toast.warning(
          claimError.response?.data?.detail ||
            t("edgeDetail.list.claimAfterCreateFailed")
        );
      }


      await fetchEdges();
    } catch (err) {

      let errorMessage = t("edgeDetail.list.createFailed");

      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;

        if (detail.includes("already exists")) {
          errorMessage = t("edgeDetail.list.nameInUse");
        } else if (detail.includes("not found")) {
          errorMessage = t("edgeDetail.list.tenantNotFound");
        } else if (detail.includes("not allowed")) {
          errorMessage = t("edgeDetail.list.notAllowed");
        } else if (typeof detail === "string") {
          errorMessage = detail;
        }
      }

      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Copy ID to clipboard
  const handleCopyId = () => {
    navigator.clipboard.writeText(createdEdgeId);
    toast.success(t("edgeDetail.list.idCopied"));
  };

  // Close success modal
  const handleCloseSuccessModal = () => {
    setSuccessModalOpen(false);
    setCreatedEdgeId("");
  };

  const openClaimModal = (claim) => {
    setClaimEdgeId(claim.edge_id);
    setClaimEdgeUuid(claim.edge_uuid);
    setClaimCode(claim.claim_code);
    setClaimExpiresAt(claim.expires_at);
    setClaimModalOpen(true);
  };

  const handleIssueClaim = async (edge) => {
    const requestId = ++claimRequest.current;
    try {
      setClaimLoadingId(edge.id);
      const claim = await issueDeviceClaim(accessToken, edge.id);
      if (requestId !== claimRequest.current) return;
      if (claim.edge_uuid !== edge.id) {
        toast.error(t("edgeDetail.list.claimWrongEdge"));
        return;
      }
      openClaimModal(claim);
    } catch (err) {
      if (requestId !== claimRequest.current) return;
      toast.error(err.response?.data?.detail || t("edgeDetail.list.claimFailed"));
    } finally {
      if (requestId === claimRequest.current) setClaimLoadingId(null);
    }
  };

  const handleCloseClaimModal = () => {
    setClaimModalOpen(false);
    setClaimCode("");
    setClaimEdgeId("");
    setClaimEdgeUuid("");
    setClaimExpiresAt("");
  };

  const handleCopyClaim = async () => {
    await navigator.clipboard.writeText(claimCode);
    toast.success(t("edgeDetail.list.claimCopied"));
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ color: "var(--text-primary)" }}>
      <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <Typography variant="h5" sx={{ fontWeight: "bold", color: "var(--text-primary)" }}>
            {t("edgeDetail.list.title")}
          </Typography>
          <Typography variant="body2" sx={{ color: "var(--text-muted)", mt: 0.5 }}>
            {isSuperAdmin ? t("edgeDetail.list.subtitleAll") : t("edgeDetail.list.subtitleMine")}
          </Typography>
        </div>

        {isSuperAdmin && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateModal}
            sx={{
              backgroundColor: "var(--success)",
              color: "#fff",
              "&:hover": { backgroundColor: "#2ea043" },
            }}
          >
            {t("edgeDetail.list.addDevice")}
          </Button>
        )}
      </Box>

      <TextField
        placeholder={t("edgeDetail.list.searchPlaceholder")}
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        size="small"
        sx={{
          mb: 2,
          minWidth: 320,
          "& .MuiInputBase-root": { color: "var(--text-primary)" },
          "& .MuiOutlinedInput-root": {
            "& fieldset": { borderColor: "var(--border)" },
            "&:hover fieldset": { borderColor: "var(--accent-navy-light)" },
          },
        }}
      />

      {edges.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 6, color: "var(--text-muted)" }}>
          <Typography>
            {search ? t("edgeDetail.list.noMatch", { q: search }) : t("edgeDetail.list.empty")}
          </Typography>
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            backgroundColor: "var(--bg-card)",
            border: "1px solid var(--border)",
            borderRadius: "8px",
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "var(--bg-hover)" }}>
                <TableCell sx={{ color: "var(--text-muted)", fontWeight: 600 }}>{t("edgeDetail.list.colName")}</TableCell>
                {isSuperAdmin && (
                  <TableCell sx={{ color: "var(--text-muted)", fontWeight: 600 }}>{t("edgeDetail.list.colTenant")}</TableCell>
                )}
                <TableCell sx={{ color: "var(--text-muted)", fontWeight: 600 }}>{t("edgeDetail.list.colLocation")}</TableCell>
                <TableCell sx={{ color: "var(--text-muted)", fontWeight: 600 }}>{t("edgeDetail.list.colAssetId")}</TableCell>
                <TableCell sx={{ color: "var(--text-muted)", fontWeight: 600 }}>{t("edgeDetail.list.colLine")}</TableCell>
                <TableCell sx={{ color: "var(--text-muted)", fontWeight: 600 }}>{t("edgeDetail.list.colModel")}</TableCell>
                <TableCell sx={{ color: "var(--text-muted)", fontWeight: 600 }}>{t("edgeDetail.list.colOs")}</TableCell>
                <TableCell sx={{ color: "var(--text-muted)", fontWeight: 600 }}>{t("edgeDetail.list.colFirmware")}</TableCell>
                <TableCell sx={{ color: "var(--text-muted)", fontWeight: 600 }}>{t("edgeDetail.list.colCreated")}</TableCell>
                <TableCell sx={{ color: "var(--text-muted)", fontWeight: 600 }} align="center">{t("edgeDetail.list.colActions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {edges.map((edge) => (
                <TableRow
                  key={edge.id}
                  sx={{
                    backgroundColor: "var(--bg-card)",
                    "&:hover": { backgroundColor: "var(--bg-hover)" },
                  }}
                >
                  <TableCell sx={{ color: "var(--text-primary)", fontWeight: 500 }}>{edge.name ?? edge.edgeId}</TableCell>
                  {isSuperAdmin && (
                    <TableCell sx={{ color: "var(--text-primary)" }}>
                      {edge.tenant_name || edge.tenantName || "-"}
                    </TableCell>
                  )}
                  <TableCell sx={{ color: "var(--text-primary)" }}>{edge.locationName || "-"}</TableCell>
                  <TableCell sx={{ color: "var(--text-primary)" }}>{edge.asset?.assetId || "-"}</TableCell>
                  <TableCell sx={{ color: "var(--text-primary)" }}>{edge.asset?.location || "-"}</TableCell>
                  <TableCell sx={{ color: "var(--text-muted)" }}>{edge.device?.model || "-"}</TableCell>
                  <TableCell sx={{ color: "var(--text-primary)", fontWeight: 500 }}>
                    {edge.reported_os_version || "—"}
                  </TableCell>
                  <TableCell sx={{ color: "var(--text-muted)" }}>{edge.device?.firmware || "-"}</TableCell>
                  <TableCell sx={{ color: "var(--text-muted)" }}>
                    {edge.created_at ? formatDate(edge.created_at, { dateStyle: "short" }) : "-"}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      sx={{ color: "var(--success)", mr: 0.5 }}
                      title={t("edgeDetail.list.goDashboard")}
                      onClick={() => navigate("/home?edgeId=" + edge.id)}
                    >
                      <DashboardIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      sx={{ color: "var(--accent-navy-light)" }}
                      title={t("edgeDetail.list.detail")}
                      onClick={() => navigate(`/edges/${edge.id}`)}
                    >
                      <VisibilityIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                    {canIssueClaim && (
                      <IconButton
                        size="small"
                        sx={{ color: "#d29922", ml: 0.5 }}
                        title={t("edgeDetail.list.issueClaimTitle")}
                        aria-label={t("edgeDetail.list.issueClaimAria", { name: edge.name ?? edge.edgeId })}
                        disabled={claimLoadingId !== null}
                        onClick={() => handleIssueClaim(edge)}
                      >
                        <VpnKeyIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Box sx={{ display: "flex", gap: 2, alignItems: "center", mt: 2 }}>
        <Button size="small" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
          {t("edgeDetail.list.prev")}
        </Button>
        <Typography variant="body2" sx={{ color: "var(--text-muted)" }}>
          {total === 0 ? "0" : `${page * PAGE_SIZE + 1}-${Math.min((page + 1) * PAGE_SIZE, total)}`} / {total}
        </Typography>
        <Button size="small" disabled={(page + 1) * PAGE_SIZE >= total} onClick={() => setPage((p) => p + 1)}>
          {t("edgeDetail.list.next")}
        </Button>
      </Box>

      {/* CREATE MODAL */}
      <Dialog
        open={createModalOpen}
        onClose={handleCloseCreateModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "var(--bg-card)",
            color: "var(--text-primary)",
            border: "1px solid var(--border)",
          },
        }}
      >
        <DialogTitle sx={{ borderBottom: "1px solid var(--border)" }}>
          {t("edgeDetail.list.createTitle")}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ color: "var(--text-muted)", mb: 3 }}>
            {t("edgeDetail.list.deviceInfo")}
          </Typography>

          {/* Tenant Dropdown */}
          <TextField
            select
            fullWidth
            label={t("edgeDetail.list.fieldTenant")}
            value={selectedTenantId}
            onChange={(e) => setSelectedTenantId(e.target.value)}
            sx={{
              mb: 2,
              "& .MuiInputBase-root": { color: "var(--text-primary)" },
              "& .MuiInputLabel-root": { color: "var(--text-muted)" },
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "var(--border)" },
                "&:hover fieldset": { borderColor: "var(--accent-navy-light)" },
              },
            }}
          >
            {tenants.map((tn) => (
              <MenuItem key={tn.id} value={tn.id}>
                {tn.name}
              </MenuItem>
            ))}
          </TextField>

          {/* Edge ID */}
          <TextField
            fullWidth
            label={t("edgeDetail.list.fieldName")}
            value={edgeId}
            onChange={(e) => setEdgeId(e.target.value)}
            sx={{
              mb: 2,
              "& .MuiInputBase-root": { color: "var(--text-primary)" },
              "& .MuiInputLabel-root": { color: "var(--text-muted)" },
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "var(--border)" },
                "&:hover fieldset": { borderColor: "var(--accent-navy-light)" },
              },
            }}
          />

          {/* Model and Firmware */}
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              fullWidth
              label={t("edgeDetail.list.colModel")}
              value={model}
              onChange={(e) => setModel(e.target.value)}
              sx={{
                "& .MuiInputBase-root": { color: "var(--text-primary)" },
                "& .MuiInputLabel-root": { color: "var(--text-muted)" },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "var(--border)" },
                  "&:hover fieldset": { borderColor: "var(--accent-navy-light)" },
                },
              }}
            />
            <TextField
              fullWidth
              label={t("edgeDetail.list.colFirmware")}
              value={firmware}
              onChange={(e) => setFirmware(e.target.value)}
              sx={{
                "& .MuiInputBase-root": { color: "var(--text-primary)" },
                "& .MuiInputLabel-root": { color: "var(--text-muted)" },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "var(--border)" },
                  "&:hover fieldset": { borderColor: "var(--accent-navy-light)" },
                },
              }}
            />
          </Box>

          {me?.global_role === "superadmin" && (<>
          <FormControlLabel
            sx={{ mt: 2, color: "var(--text-primary)" }}
            control={
              <Switch
                checked={cloudTransferEnabled}
                onChange={(event) => setCloudTransferEnabled(event.target.checked)}
                color="success"
                inputProps={{ "aria-label": t("edgeDetail.list.cloudAria") }}
              />
            }
            label={t("edgeDetail.list.cloudAria")}
          />
          <Typography variant="caption" sx={{ color: "var(--text-muted)", display: "block" }}>
            {t("edgeDetail.list.cloudHint")}
          </Typography>
          </>)}
        </DialogContent>
        <DialogActions sx={{ borderTop: "1px solid var(--border)", p: 2 }}>
                  <Button onClick={handleCloseCreateModal} sx={{ color: "var(--text-muted)" }}>
                    {t("edgeDetail.list.cancel")}
                  </Button>
                  <Button
                    onClick={handleSubmitCreate}
                    variant="contained"
                    disabled={submitting}
                    sx={{
                      backgroundColor: "var(--btn-primary)",
                      "&:hover": { backgroundColor: "var(--btn-primary-hover)" },
                    }}
                  >
                    {submitting ? t("edgeDetail.list.creating") : t("edgeDetail.list.create")}
                  </Button>
                </DialogActions>
              </Dialog>

              {/* SUCCESS MODAL - shows the created id */}
              <Dialog
                open={successModalOpen}
                onClose={handleCloseSuccessModal}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                  sx: {
                    backgroundColor: "var(--bg-card)",
                    color: "var(--text-primary)",
                    border: "1px solid var(--border)",
                  },
                }}
              >
                <DialogTitle sx={{ borderBottom: "1px solid var(--border)", color: "var(--success)" }}>
                  {t("edgeDetail.list.createdTitle")}
                </DialogTitle>
                <DialogContent sx={{ mt: 3 }}>
                  <Typography variant="body2" sx={{ color: "var(--text-muted)", mb: 1 }}>
                    {t("edgeDetail.list.deviceId")}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      p: 2,
                      backgroundColor: "var(--bg-surface)",
                      border: "1px solid var(--border)",
                      borderRadius: "6px",
                    }}
                  >
                    <Typography
                      sx={{
                        flex: 1,
                        fontFamily: "monospace",
                        fontSize: "16px",
                        color: "var(--accent-navy-light)",
                        fontWeight: "bold",
                      }}
                    >
                      {createdEdgeId}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={handleCopyId}
                      sx={{ color: "var(--text-muted)", "&:hover": { color: "var(--text-primary)" } }}
                      title={t("edgeDetail.list.copyId")}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <Typography variant="body2" sx={{ color: "var(--text-muted)", mt: 2 }}>
                    {t("edgeDetail.list.enterId")}
                  </Typography>
                </DialogContent>
                <DialogActions sx={{ borderTop: "1px solid var(--border)", p: 2 }}>
                  <Button
                    onClick={handleCloseSuccessModal}
                    variant="contained"
                    sx={{
                      backgroundColor: "var(--btn-primary)",
                      "&:hover": { backgroundColor: "var(--btn-primary-hover)" },
                    }}
                  >
                    {t("edgeDetail.list.ok")}
                  </Button>
                </DialogActions>
              </Dialog>

              {/* ONE-TIME DEVICE CLAIM MODAL */}
              <Dialog
                open={claimModalOpen}
                onClose={handleCloseClaimModal}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                  sx: {
                    backgroundColor: "var(--bg-card)",
                    color: "var(--text-primary)",
                    border: "1px solid var(--border)",
                  },
                }}
              >
                <DialogTitle sx={{ borderBottom: "1px solid var(--border)", color: "#d29922" }}>
                  {t("edgeDetail.list.enrollTitle")}
                </DialogTitle>
                <DialogContent sx={{ mt: 3 }}>
                  <Alert severity="warning" sx={{ mb: 3 }}>
                              {t("edgeDetail.list.claimWarn")}
                            </Alert>
                            <Typography variant="body2" sx={{ color: "var(--text-muted)", mb: 1 }}>
                              {t("edgeDetail.list.claimEdgeName")}
                            </Typography>
                            <Typography sx={{ fontFamily: "monospace", mb: 2, wordBreak: "break-all" }}>
                              {claimEdgeId}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "var(--text-muted)", mb: 1 }}>
                              {t("edgeDetail.list.claimUuid")}
                            </Typography>
                            <Typography sx={{ fontFamily: "monospace", mb: 2, wordBreak: "break-all" }}>
                              {claimEdgeUuid}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "var(--text-muted)", mb: 1 }}>
                              {t("edgeDetail.list.claimOnce")}
                            </Typography>
                            <Box
                              sx={{
                                p: 2,
                                backgroundColor: "var(--bg-surface)",
                                border: "1px solid var(--border)",
                                borderRadius: "6px",
                                fontFamily: "monospace",
                                wordBreak: "break-all",
                              }}
                            >
                              {claimCode}
                            </Box>
                            <Typography variant="caption" sx={{ color: "var(--text-muted)", mt: 2, display: "block" }}>
                              {t("edgeDetail.list.claimExpires", { date: claimExpiresAt ? formatDate(claimExpiresAt, { dateStyle: "short", timeStyle: "medium" }) : "—" })}
                            </Typography>
                          </DialogContent>
                          <DialogActions sx={{ borderTop: "1px solid var(--border)", p: 2 }}>
                            <Button onClick={handleCopyClaim} startIcon={<ContentCopyIcon />} sx={{ color: "var(--accent-navy-light)" }}>
                              {t("edgeDetail.list.copyClaim")}
                            </Button>
                            <Button onClick={handleCloseClaimModal} variant="contained" sx={{ backgroundColor: "var(--btn-primary)", "&:hover": { backgroundColor: "var(--btn-primary-hover)" } }}>
                              {t("edgeDetail.list.closeAndClear")}
                            </Button>
                          </DialogActions>
                        </Dialog>
                      </Box>
                    );
                  }
